import { CartAddEvent, CartErrorEvent, ThemeEvents } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

class AvtInstallPackage extends HTMLElement {
  #activeOperationId = null;

  #requiresCartReview = false;

  connectedCallback() {
    this.addEventListener('click', this.#handleClick);
    document.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    document.addEventListener('avt:fitment-change', this.#handleFitmentChange);
    window.addEventListener('storage', this.#handleFitmentChange);
    this.#applyFitmentCandidates();
    this.#applyManufacturerDocuments(this.dataset.primaryVariantSku);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    document.removeEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    document.removeEventListener('avt:fitment-change', this.#handleFitmentChange);
    window.removeEventListener('storage', this.#handleFitmentChange);
  }

  #handleClick = (event) => {
    const button = event.target instanceof Element
      ? event.target.closest('[data-avt-package-add]')
      : null;
    if (!(button instanceof HTMLButtonElement) || !this.contains(button)) return;
    this.#addSelectedItems(button);
  };

  #handleVariantUpdate = (event) => {
    if (String(event.detail?.data?.productId ?? '') !== String(this.dataset.productId ?? '')) return;
    const variant = event.detail?.resource;
    this.dataset.primaryVariantId = String(variant?.id ?? '');
    this.dataset.primaryVariantSku = String(variant?.sku ?? '');
    this.dataset.primaryAvailable = String(variant?.available === true);
    const button = this.querySelector('[data-avt-package-add]');
    if (button instanceof HTMLButtonElement) {
      button.disabled = variant?.available !== true || this.#requiresCartReview || Boolean(this.#activeOperationId);
    }
    this.#applyManufacturerDocuments(variant?.sku);
  };

  #handleFitmentChange = (event) => {
    if (event instanceof StorageEvent && event.key !== 'avt-fitment-selection') return;
    this.#applyFitmentCandidates();
  };

  #applyManufacturerDocuments(sku) {
    const section = this.querySelector('[data-avt-manufacturer-documents]');
    const links = this.querySelector('[data-avt-manufacturer-document-links]');
    if (!(section instanceof HTMLElement) || !(links instanceof HTMLElement)) return;

    const template = [...this.querySelectorAll('[data-avt-manufacturer-document-template]')]
      .find((candidate) => candidate instanceof HTMLTemplateElement
        && candidate.dataset.variantSku === String(sku ?? ''));

    if (!(template instanceof HTMLTemplateElement)) {
      section.hidden = links.childElementCount === 0;
      return;
    }

    links.replaceChildren(template.content.cloneNode(true));
    section.hidden = links.childElementCount === 0;
  }

  #applyFitmentCandidates() {
    let selection = {};
    try {
      selection = JSON.parse(window.localStorage.getItem('avt-fitment-selection')) || {};
    } catch {
      selection = {};
    }
    const hasMotorcycle = Boolean(selection.make && selection.model && selection.year);
    const compatibilityKeys = new Set(hasMotorcycle
      ? [
          `${selection.make}::${selection.model}::${selection.year}`,
          ...(Array.isArray(selection.compatibilityKeys) ? selection.compatibilityKeys : []),
        ]
      : []);

    const applyCandidate = (candidate, displayAllowed = true) => {
      if (!(candidate instanceof HTMLElement)) return false;
      const requiresFitment = candidate.dataset.fitmentRelation === 'same_motorcycle';
      const input = candidate.querySelector('[data-avt-package-item]');
      const status = candidate.querySelector('[data-avt-fitment-candidate-status]');
      let compatibility = [];
      try {
        const parsed = JSON.parse(candidate.dataset.fitmentCompatibility || '[]');
        compatibility = Array.isArray(parsed) ? parsed : [];
      } catch {
        compatibility = [];
      }
      const matches = !requiresFitment || (hasMotorcycle && (
        compatibility.includes('universal')
        || compatibility.some((key) => compatibilityKeys.has(key))
      ));
      const selectable = displayAllowed && matches;
      candidate.hidden = !displayAllowed || (requiresFitment && hasMotorcycle && !matches);
      if (input instanceof HTMLInputElement) {
        input.disabled = !selectable;
        if (!selectable) input.checked = false;
        else if (input.hasAttribute('data-default-selected')) input.checked = true;
      }
      if (status instanceof HTMLElement) {
        status.textContent = hasMotorcycle
          ? matches ? 'Fits your saved bike.' : ''
          : 'Choose your bike to confirm fitment.';
      }
      return !requiresFitment || !hasMotorcycle || matches;
    };

    const automaticCandidates = new Set();
    this.querySelectorAll('[data-avt-automatic-candidates]').forEach((container) => {
      if (!(container instanceof HTMLElement)) return;
      const configuredLimit = Number(container.dataset.displayLimit);
      const displayLimit = Number.isInteger(configuredLimit) && configuredLimit > 0
        ? configuredLimit
        : 6;
      let displayed = 0;
      container.querySelectorAll('[data-avt-package-candidate]').forEach((candidate) => {
        automaticCandidates.add(candidate);
        const eligible = applyCandidate(candidate, displayed < displayLimit);
        if (eligible && displayed < displayLimit) displayed += 1;
        if (!eligible) applyCandidate(candidate, false);
      });
    });

    this.querySelectorAll('[data-avt-package-candidate]').forEach((candidate) => {
      if (automaticCandidates.has(candidate)) return;
      applyCandidate(candidate);
    });
  }

  async #readCart() {
    try {
      const response = await fetch(`${Theme.routes.cart_url}.js`, {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: {
          Accept: 'application/json',
          'Cache-Control': 'no-cache',
        },
      });
      if (!response.ok) return null;
      const cart = await response.json();
      return Array.isArray(cart?.items) ? cart : null;
    } catch {
      return null;
    }
  }

  #operationLines(cart, operationId) {
    return cart.items.filter((item) => (
      String(item?.properties?._avt_install_package_op ?? '') === operationId
    ));
  }

  #operationComplete(cart, expectedItems, operationId) {
    const expected = new Map(expectedItems.map(({ id, quantity }) => [String(id), quantity]));
    const actual = new Map();
    this.#operationLines(cart, operationId).forEach((item) => {
      const id = String(item.variant_id ?? item.id ?? '');
      actual.set(id, (actual.get(id) ?? 0) + Number(item.quantity ?? 0));
    });
    return actual.size === expected.size
      && [...expected].every(([id, quantity]) => actual.get(id) === quantity);
  }

  async #rollbackOperation(cart, operationId) {
    const lines = this.#operationLines(cart, operationId);
    for (const line of lines) {
      if (!line.key) return false;
      try {
        const response = await fetch(Theme.routes.cart_change_url, {
          ...fetchConfig('json', { body: JSON.stringify({ id: line.key, quantity: 0 }) }),
        });
        const data = await response.json();
        if (!response.ok || data?.errors) return false;
      } catch {
        return false;
      }
    }

    const readback = await this.#readCart();
    return Boolean(readback && this.#operationLines(readback, operationId).length === 0);
  }

  async #addSelectedItems(button) {
    if (this.#activeOperationId || this.#requiresCartReview) return;

    const primaryVariantId = Number(this.dataset.primaryVariantId);
    if (!Number.isSafeInteger(primaryVariantId) || this.dataset.primaryAvailable !== 'true') {
      this.#setStatus('Select an available primary product variant before adding the package.', true);
      return;
    }

    const operationId = window.crypto?.randomUUID?.();
    if (!operationId) {
      this.#setStatus('This package could not be given a safe cart operation ID. Refresh and try again.', true);
      return;
    }

    const selectedCompanions = [...this.querySelectorAll('[data-avt-package-item]:checked')]
      .filter((input) => input instanceof HTMLInputElement && !input.disabled)
      .map((input) => ({
        id: Number(input.value),
        quantity: Number(input.dataset.quantity ?? 1),
      }))
      .filter(({ id, quantity }) => Number.isSafeInteger(id) && Number.isInteger(quantity) && quantity > 0);

    if (selectedCompanions.length === 0) {
      this.#setStatus('Select at least one available companion, or use the regular Add to cart button.', true);
      return;
    }

    const quantities = new Map([[primaryVariantId, 1]]);
    for (const { id, quantity } of selectedCompanions) {
      quantities.set(id, (quantities.get(id) ?? 0) + quantity);
    }
    const items = [...quantities].map(([id, quantity]) => ({ id, quantity }));
    const operationItems = items.map((item) => ({
      ...item,
      properties: { _avt_install_package_op: operationId },
    }));
    const sectionIds = [...document.querySelectorAll('cart-items-component[data-section-id]')]
      .map((component) => component instanceof HTMLElement ? component.dataset.sectionId : null)
      .filter(Boolean)
      .filter((sectionId, index, values) => values.indexOf(sectionId) === index)
      .slice(0, 5);
    const body = { items: operationItems };
    if (sectionIds.length) {
      body.sections = sectionIds.join(',');
      body.sections_url = window.location.pathname;
    }

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    this.#activeOperationId = operationId;
    this.#setStatus('Adding selected items...');

    try {
      let data = {};
      let addFailure = '';
      try {
        const response = await fetch(Theme.routes.cart_add_url, {
          ...fetchConfig('json', { body: JSON.stringify(body) }),
        });
        data = await response.json();
        if (!response.ok || data.status) {
          addFailure = String(data.description || data.message || 'The selected items could not be added.');
        }
      } catch (error) {
        addFailure = error instanceof Error
          ? error.message
          : 'The selected items could not be added.';
      }

      const cart = await this.#readCart();
      const addedItemCount = items.reduce((total, item) => total + item.quantity, 0);
      if (cart && this.#operationComplete(cart, items, operationId)) {
        this.dispatchEvent(new CartAddEvent(cart, this.id, {
          source: 'avt-install-package',
          itemCount: cart.item_count,
          productId: this.dataset.productId,
          variantId: String(primaryVariantId),
          sections: data.sections,
        }));
        let bikeKey = '';
        try {
          const selection = JSON.parse(
            window.localStorage.getItem('avt-fitment-selection'),
          ) || {};
          bikeKey = selection.make && selection.model && selection.year
            ? `${selection.make}::${selection.model}::${selection.year}`
            : '';
        } catch {
          bikeKey = '';
        }
        window.AVTJourney?.publish('avt:project_basket_added', {
          bike_key: bikeKey,
          item_count: addedItemCount,
          product_id: this.dataset.productId,
          source: 'install_package',
          variant_id: String(primaryVariantId),
        });
        this.#setStatus(`${addedItemCount} items added to your cart.`);
        return;
      }

      let message = addFailure || 'The selected items could not be verified in your cart.';
      const partialLines = cart ? this.#operationLines(cart, operationId) : [];
      if (partialLines.length > 0) {
        if (await this.#rollbackOperation(cart, operationId)) {
          message = 'Some package items were added, then removed because the full package was unavailable. Review availability and try again.';
        } else {
          this.#requiresCartReview = true;
          message = 'Some package items may still be in your cart. Review the cart before trying this package again.';
        }
      } else if (!cart) {
        this.#requiresCartReview = true;
        message = 'The cart could not be read back. Review the cart before trying this package again.';
      }

      this.dispatchEvent(new CartErrorEvent(this.id, 'Cart error', message, data.errors));
      this.#setStatus(message, true);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'The selected items could not be added.';
      this.dispatchEvent(new CartErrorEvent(this.id, 'Cart error', message));
      this.#setStatus(message, true);
    } finally {
      if (this.#activeOperationId === operationId) this.#activeOperationId = null;
      button.removeAttribute('aria-busy');
      button.disabled = this.#requiresCartReview || this.dataset.primaryAvailable !== 'true';
    }
  }

  #setStatus(message, isError = false) {
    const status = this.querySelector('[data-avt-package-status]');
    if (!(status instanceof HTMLElement)) return;
    status.textContent = message;
    status.dataset.status = isError ? 'error' : 'success';
  }
}

if (!customElements.get('avt-install-package')) {
  customElements.define('avt-install-package', AvtInstallPackage);
}
